export const ACCOUNT_GROUPS = ["Assets", "Liabilities", "Revenue", "Expenses"];

export const FEE_TYPES = [
  "None",
  "Subscription",
  "Backtrack",
  "Pulse",
  "Handling",
  "Inbound",
  "Outbound",
  "Overstock",
  "Pick & Pack",
  "Material",
  "Large Order",
  "Transportation",
  "Order Pickup",
  "Repacking",
  "Balance Recharge",
  "Other",
];

export const EXPENSE_CATEGORIES = ["payroll", "rent", "shipping", "utilities", "technology", "other"];
export const EXPENSE_METHODS = ["Cash", "Bank Transfer", "Card", "Instapay", "Cheque", "Other"];
export const EXPENSE_STATUSES = ["Processing", "Paid"];

export const BILLING_CADENCE = ["Monthly", "Quarterly", "Yearly"];
export const MEMBERSHIP_STATUS = ["Active", "Trial"];

export const REPORT_TYPES = [
  { value: "profit-and-loss", label: "Profit & Loss" },
  { value: "balance-sheet", label: "Balance Sheet" },
  { value: "cash-flow", label: "Cash Flow" },
  { value: "brand-profitability", label: "Brand Profitability" },
  { value: "invoice-aging", label: "Invoice Aging" },
  { value: "tax", label: "Tax" },
];

export const AUDIT_KINDS = ["fee", "approval", "payment", "void"];

export const DUE_STATUSES = [
  "Due Today",
  "Due This Week",
  "Overdue",
  "Partially Paid",
  "Fully Paid",
  "Upcoming",
  "Escalated",
  "Settled",
];

const PAYOUT_MOVES = {
  Created: ["Reviewed", "Failed", "Overdue", "Escalated"],
  Reviewed: ["Approved", "Failed", "Overdue", "Escalated"],
  Approved: ["Processing", "Failed", "Overdue", "Escalated"],
  Processing: ["Paid", "Failed", "Overdue", "Escalated"],
  Failed: ["Reviewed", "Processing", "Escalated"],
  Overdue: ["Reviewed", "Processing", "Escalated", "Paid"],
  Escalated: ["Reviewed", "Approved", "Processing", "Paid"],
  Paid: [],
};

export const nextPayoutStages = (stage) => PAYOUT_MOVES[stage] || [];

export const invoiceActions = (status) => ({
  edit: status !== "Paid",
  editVendor: status === "Draft",
  send: ["Draft", "Sent", "Due", "Overdue"].includes(status),
  remind: status !== "Draft" && status !== "Paid",
  paid: status !== "Paid",
  escalate: ["Sent", "Due", "Overdue"].includes(status),
  pdf: true,
});

export const feeActions = (status) => ({
  edit: status === "Pending",
  apply: status === "Pending",
  invoice: status === "Applied",
});

export const compensationActions = (status) => ({
  decide: status === "Pending Approval",
});

export const asList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.docs)) return payload.docs;
  return [];
};

export const pageMeta = (payload) => ({
  docs: payload?.docs || [],
  total: payload?.totalDocs || 0,
  page: payload?.page || 1,
  limit: payload?.limit || 20,
});

export const changedAmounts = (original, next, keys) => {
  const body = {};
  for (const key of keys) {
    if (String(next?.[key] ?? "") === String(original?.[key] ?? "")) continue;
    const value = Number(next[key]);
    if (!Number.isFinite(value) || value < 0) {
      return { error: "Amounts must be zero or greater." };
    }
    body[key] = value;
  }
  return { body };
};

export const RATE_CARD_FIELDS = [
  { key: "fulfillmentFee", label: "Fulfillment fee", group: "Handling", hint: "Handling base. Defaults to the tier fee." },
  { key: "discount", label: "Discount", group: "Handling", hint: "Subtracted from fulfillment. Defaults to 0." },
  { key: "returnFee", label: "Return fee", group: "Handling", hint: "Handling on return orders. Defaults to net fulfillment." },
  { key: "shippingMultiplier", label: "Shipping multiplier", group: "Handling", hint: "Inhouse shipping fee times this multiplier.", unit: "x" },
  { key: "inboundFeePerPiece", label: "Inbound per piece", group: "Warehouse", hint: "Defaults to 1 EGP." },
  { key: "outboundFeePerPiece", label: "Outbound per piece", group: "Warehouse", hint: "Defaults to 3 EGP." },
  { key: "largeOrderFeePerPiece", label: "Large order per piece", group: "Warehouse", hint: "Defaults to 1 EGP." },
  { key: "largeOrderFreePieces", label: "Large order free pieces", group: "Warehouse", hint: "Pieces included before the large-order fee. Defaults to 4.", unit: "pcs" },
  { key: "storageFee", label: "Storage per pallet day", group: "Warehouse", hint: "Overstock price. Defaults to the common overstock fee." },
  { key: "backtrackFees", label: "Backtrack", group: "Plans", hint: "Monthly base. Defaults to common fees." },
  { key: "backtrackLvl2Fees", label: "Backtrack level 2", group: "Plans", hint: "201–1500 requests." },
  { key: "backtrackLvl3Fees", label: "Backtrack level 3", group: "Plans", hint: "1500+ requests." },
  { key: "pulseFees", label: "Pulse", group: "Plans", hint: "Pulse Unlimited monthly. Defaults to common fees, otherwise 150." },
  { key: "pickAndPackFee", label: "Pick & pack", group: "Additional", hint: "Default unit price for Pick & Pack fees." },
  { key: "materialFee", label: "Material", group: "Additional", hint: "Default unit price for Material fees." },
  { key: "transportationFee", label: "Transportation", group: "Additional", hint: "Default unit price for Transportation fees." },
  { key: "orderPickupFee", label: "Order pickup", group: "Additional", hint: "Default unit price for Order Pickup fees." },
  { key: "repackingFee", label: "Repacking", group: "Additional", hint: "Default unit price for Repacking fees." },
  { key: "subscriptionFee", label: "Subscription", group: "Additional", hint: "Default unit price for Subscription fees." },
  { key: "balanceRechargeFee", label: "Balance recharge", group: "Additional", hint: "Default unit price for Balance Recharge fees." },
  { key: "otherFee", label: "Other", group: "Additional", hint: "Default unit price for Other fees." },
];

const RATE_CARD_BY_KEY = Object.fromEntries(RATE_CARD_FIELDS.map((field) => [field.key, field]));

const humanizeKey = (key) => key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

export const rateCardFieldList = (payload) => {
  const raw = payload?.rateCardFields;
  const apiKeys = Array.isArray(raw)
    ? raw.map((item) => (typeof item === "string" ? item : item?.key || item?.field || item?.name)).filter(Boolean)
    : [];
  const keys = apiKeys.length ? apiKeys : RATE_CARD_FIELDS.map((field) => field.key);
  return keys.map((key) => RATE_CARD_BY_KEY[key] || { key, label: humanizeKey(key), group: "Other", hint: "" });
};

const hasOverride = (override) => override != null && override !== "";

export const rateCardDraft = (brand, fields) => {
  const draft = {};
  for (const field of fields) {
    const override = brand?.overrides?.[field.key];
    const resolved = brand?.[field.key];
    draft[field.key] = {
      value: hasOverride(override) ? String(override) : (resolved == null ? "" : String(resolved)),
      inherit: !hasOverride(override),
    };
  }
  return draft;
};

export const rateCardPatch = (brand, draft, keys) => {
  const body = {};
  for (const key of keys) {
    const entry = draft?.[key] || {};
    const override = brand?.overrides?.[key];
    if (entry.inherit || entry.value === "" || entry.value == null) {
      if (hasOverride(override)) body[key] = null;
      continue;
    }
    const value = Number(entry.value);
    if (!Number.isFinite(value) || value < 0) {
      return { error: "Amounts must be zero or greater." };
    }
    const baseline = hasOverride(override) ? Number(override) : Number(brand?.[key]);
    if (value !== baseline) body[key] = value;
  }
  return { body };
};

export const formatStamp = (value) => {
  if (value == null || value === "") return "—";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString("en-GB", { hour12: false });
};

export const describeValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "—";
    return entries.map(([key, item]) => `${key}: ${item == null || item === "" ? "—" : typeof item === "object" ? JSON.stringify(item) : item}`).join(" · ");
  }
  return String(value);
};

export const monthBounds = (months) => {
  if (!months?.length) return {};
  const first = months[0].month;
  const last = months[months.length - 1].month;
  const [ys, ms] = first.split("-").map(Number);
  const [ye, me] = last.split("-").map(Number);
  const end = new Date(ye, me, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    startDate: `${ys}-${pad(ms)}-01`,
    endDate: `${ye}-${pad(me)}-${pad(end.getDate())}`,
  };
};
